import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  FileText,
  Filter,
  Plus,
  Printer,
  ReceiptText,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";

const STORAGE_KEY = "notaris_invoices";

const defaultItems = [
  { id: 1, description: "Biaya Akta", unit: "Rp. 2.500.000,-", quantity: "1", amount: "" },
];

const initialForm = {
  clientName: "",
  subject: "",
  spelledAmount: "",
  note:
    "Biaya dapat ditransfer melalui BCA dengan nomor rekening 1236809090 atas nama ERWIN SUHARDIMAN.",
  city: "Kepanjen",
  signerTitle: "Notaris / PPAT di Kabupaten Malang",
  extraDescription: "",
  items: defaultItems,
};

const cloneItems = (items) => items.map((item) => ({ ...item }));

const cloneInitialForm = () => ({ ...initialForm, items: cloneItems(defaultItems) });

const fieldClass =
  "w-full rounded-lg border c-border bg-white px-3 py-2.5 text-sm text-ink placeholder:text-[#7A828A] outline-none transition focus:border-gold focus:ring-2 focus:ring-gold/20 dark:bg-[#10171E] dark:text-[#ECE9E2] dark:placeholder:text-[#9AA1A8]";

const formatCurrency = (value) => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return "Rp. 0,-";
  }

  return `Rp. ${new Intl.NumberFormat("id-ID").format(number)},-`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const parseAmount = (value) => {
  if (typeof value === "number") return value;
  return Number(String(value || "").replace(/[^\d]/g, "")) || 0;
};

const getItemTotal = (item) => {
  const manualAmount = parseAmount(item.amount);
  if (manualAmount > 0) return manualAmount;

  const unitAmount = parseAmount(item.unit);
  const quantity = parseAmount(item.quantity) || 1;
  return unitAmount * quantity;
};

const getInvoiceTotal = (items = []) => items.reduce((sum, item) => sum + getItemTotal(item), 0);

const numberWords = [
  "",
  "satu",
  "dua",
  "tiga",
  "empat",
  "lima",
  "enam",
  "tujuh",
  "delapan",
  "sembilan",
  "sepuluh",
  "sebelas",
];

const toWordsRaw = (number) => {
  const value = Math.floor(Number(number) || 0);

  if (value === 0) return "nol";
  if (value < 12) return numberWords[value];
  if (value < 20) return `${toWordsRaw(value - 10)} belas`;
  if (value < 100) return `${toWordsRaw(Math.floor(value / 10))} puluh ${toWordsRaw(value % 10)}`.trim();
  if (value < 200) return `seratus ${toWordsRaw(value - 100)}`.trim();
  if (value < 1000) return `${toWordsRaw(Math.floor(value / 100))} ratus ${toWordsRaw(value % 100)}`.trim();
  if (value < 2000) return `seribu ${toWordsRaw(value - 1000)}`.trim();
  if (value < 1000000) return `${toWordsRaw(Math.floor(value / 1000))} ribu ${toWordsRaw(value % 1000)}`.trim();
  if (value < 1000000000) return `${toWordsRaw(Math.floor(value / 1000000))} juta ${toWordsRaw(value % 1000000)}`.trim();
  return `${toWordsRaw(Math.floor(value / 1000000000))} miliar ${toWordsRaw(value % 1000000000)}`.trim();
};

const toWords = (number) => toWordsRaw(number).replace(/\s*nol\b/g, "").replace(/\s+/g, " ").trim() || "nol";

const formatDateLong = (date = new Date()) =>
  new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);

const createInvoiceNumber = () => {
  const now = new Date();
  const datePart = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("");

  return `INV-${datePart}-${String(now.getTime()).slice(-4)}`;
};

function InvoicePreview({ invoice }) {
  const total = getInvoiceTotal(invoice.items);
  const spelledAmount = invoice.spelledAmount || `${toWords(total)} rupiah`;

  return (
    <div className="bg-white text-black border border-ink/10 shadow-sm rounded-lg overflow-hidden">
      <div className="invoice-paper p-6 sm:p-8 font-serif min-h-[760px]">
        <div className="flex items-start gap-4 border-b-4 border-[#6f2f2f] pb-2">
          <img
            src="/assets/garuda-invoice.png"
            alt="Garuda"
            className="h-20 w-20 shrink-0 object-contain"
          />
          <div className="flex-1 text-center leading-tight">
            <div className="text-lg sm:text-xl font-bold">NOTARIS DAN PEJABAT PEMBUAT AKTA TANAH</div>
            <div className="text-lg sm:text-xl font-bold">ERWIN SUHARDIMAN, S.Sos, S.H, M.Kn</div>
            <div className="text-sm">Jln. Panglima Suhardiman No. 08 Telp. (0341) 392211 Fax. (0341) 396586</div>
            <div className="text-sm">Kepanjen - Kabupaten Malang</div>
            <div className="text-xs font-semibold">
              SK. MENTERI HUKUM DAN HAM R I Nomor : AHU-0706.AH.02.01 Th.2010 Tanggal 22 Pebruari 2010
            </div>
            <div className="text-xs font-semibold">
              SK. KEPALA BADAN PERTANAHAN NASIONAL RI Nomor : 106/KEP- 17.3/III/2011 Tanggal 21 Maret 2011
            </div>
            <div className="text-xs italic">Email: erwin.suhardiman@yahoo.co.id</div>
          </div>
        </div>

        <h2 className="mt-6 text-center text-2xl font-bold underline tracking-wide">INVOICE</h2>

        <div className="mt-7 grid gap-1 text-base">
          {invoice.clientName && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span>KEPADA</span>
              <strong>: {invoice.clientName}</strong>
            </div>
          )}
          {invoice.subject && (
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span>KETERANGAN</span>
              <strong>: {invoice.subject}</strong>
            </div>
          )}
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <span>TERBILANG</span>
            <strong>: {spelledAmount}</strong>
          </div>
        </div>

        {invoice.extraDescription && <p className="mt-6 whitespace-pre-line text-base">{invoice.extraDescription}</p>}

        <div className="mt-6 overflow-hidden border border-black">
          <table className="w-full border-collapse text-base">
            <thead>
              <tr className="font-bold">
                <th className="w-12 border-b border-r border-black px-2 py-1 text-center">NO</th>
                <th className="border-b border-r border-black px-2 py-1 text-left">PEMBAYARAN</th>
                <th className="w-40 border-b border-black px-2 py-1 text-left">JUMLAH</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id}>
                  <td className="border-r border-black px-2 py-3 align-top text-center">{index + 1}.</td>
                  <td className="border-r border-black px-2 py-3 align-top whitespace-pre-line">{item.description}</td>
                  <td className="px-2 py-3 align-top">{formatCurrency(getItemTotal(item))}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td colSpan={2} className="border-t border-r border-black px-2 py-1 text-center">
                  TOTAL
                </td>
                <td className="border-t border-black px-2 py-1">{formatCurrency(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {invoice.note && <p className="mt-5 whitespace-pre-line text-base">{invoice.note}</p>}

        <div className="mt-12 ml-auto w-72 text-center text-base">
          <div>
            {invoice.city}, {invoice.createdDateLabel}
          </div>
          <div>Mengetahui,</div>
          <div className="h-24" />
          <div className="font-bold underline">ERWIN SUHARDIMAN, S.Sos., S.H., M.Kn</div>
          <div>{invoice.signerTitle}</div>
        </div>
      </div>
    </div>
  );
}

const buildPrintableInvoiceHtml = (invoice) => {
  const total = getInvoiceTotal(invoice.items);
  const spelledAmount = invoice.spelledAmount || `${toWords(total)} rupiah`;
  const rows = invoice.items
    .map(
      (item, index) => `
        <tr>
          <td class="number-cell">${index + 1}.</td>
          <td class="description-cell">${escapeHtml(item.description).replace(/\n/g, "<br />")}</td>
          <td class="amount-cell">${formatCurrency(getItemTotal(item))}</td>
        </tr>
      `
    )
    .join("");

  return `<!doctype html>
    <html>
      <head>
        <title></title>
        <style>
          @page { size: A4; margin: 0; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; background: #fff; color: #000; }
          body { font-family: "Times New Roman", serif; }
          .paper { width: 210mm; min-height: 297mm; padding: 14mm 16mm; }
          .header { display: flex; gap: 12px; align-items: flex-start; border-bottom: 4px solid #6f2f2f; padding-bottom: 8px; }
          .logo { width: 72px; height: 72px; object-fit: contain; flex: 0 0 auto; }
          .header-text { flex: 1; text-align: center; line-height: 1.12; }
          .office-title { font-size: 20px; font-weight: 700; }
          .office-name { font-size: 20px; font-weight: 700; }
          .office-line { font-size: 13px; }
          .office-small { font-size: 11px; font-weight: 700; }
          .email { font-size: 11px; font-style: italic; }
          h1 { margin: 24px 0 22px; text-align: center; font-size: 26px; text-decoration: underline; letter-spacing: 1px; }
          .meta { display: grid; gap: 4px; font-size: 16px; margin-bottom: 22px; }
          .meta-row { display: grid; grid-template-columns: 110px 1fr; gap: 8px; }
          .preline { white-space: pre-line; font-size: 16px; margin: 18px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 16px; }
          th, td { border: 1px solid #000; padding: 7px 8px; vertical-align: top; }
          th { font-weight: 700; text-align: left; }
          .number-cell { width: 42px; text-align: center; }
          .amount-cell { width: 155px; }
          .total-label { text-align: center; font-weight: 700; }
          .total-amount { font-weight: 700; }
          .note { margin-top: 18px; white-space: pre-line; font-size: 16px; }
          .signature { margin-top: 44px; margin-left: auto; width: 285px; text-align: center; font-size: 16px; }
          .signature-space { height: 88px; }
          .signer { font-weight: 700; text-decoration: underline; }
        </style>
      </head>
      <body>
        <main class="paper">
          <section class="header">
            <img class="logo" src="${window.location.origin}/assets/garuda-invoice.png" alt="" />
            <div class="header-text">
              <div class="office-title">NOTARIS DAN PEJABAT PEMBUAT AKTA TANAH</div>
              <div class="office-name">ERWIN SUHARDIMAN, S.Sos, S.H, M.Kn</div>
              <div class="office-line">Jln. Panglima Suhardiman No. 08 Telp. (0341) 392211 Fax. (0341) 396586</div>
              <div class="office-line">Kepanjen - Kabupaten Malang</div>
              <div class="office-small">SK. MENTERI HUKUM DAN HAM R I Nomor : AHU-0706.AH.02.01 Th.2010 Tanggal 22 Pebruari 2010</div>
              <div class="office-small">SK. KEPALA BADAN PERTANAHAN NASIONAL RI Nomor : 106/KEP- 17.3/III/2011 Tanggal 21 Maret 2011</div>
              <div class="email">Email: erwin.suhardiman@yahoo.co.id</div>
            </div>
          </section>
          <h1>INVOICE</h1>
          <section class="meta">
            ${invoice.clientName ? `<div class="meta-row"><span>KEPADA</span><strong>: ${escapeHtml(invoice.clientName)}</strong></div>` : ""}
            ${invoice.subject ? `<div class="meta-row"><span>KETERANGAN</span><strong>: ${escapeHtml(invoice.subject)}</strong></div>` : ""}
            <div class="meta-row"><span>TERBILANG</span><strong>: ${escapeHtml(spelledAmount)}</strong></div>
          </section>
          ${invoice.extraDescription ? `<p class="preline">${escapeHtml(invoice.extraDescription)}</p>` : ""}
          <table>
            <thead>
              <tr>
                <th class="number-cell">NO</th>
                <th>PEMBAYARAN</th>
                <th class="amount-cell">JUMLAH</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
              <tr>
                <td class="total-label" colspan="2">TOTAL</td>
                <td class="total-amount">${formatCurrency(total)}</td>
              </tr>
            </tbody>
          </table>
          ${invoice.note ? `<p class="note">${escapeHtml(invoice.note)}</p>` : ""}
          <section class="signature">
            <div>${escapeHtml(invoice.city)}, ${escapeHtml(invoice.createdDateLabel)}</div>
            <div>Mengetahui,</div>
            <div class="signature-space"></div>
            <div class="signer">ERWIN SUHARDIMAN, S.Sos., S.H., M.Kn</div>
            <div>${escapeHtml(invoice.signerTitle)}</div>
          </section>
        </main>
      </body>
    </html>`;
};

export default function Invoice() {
  const [form, setForm] = useState(() => cloneInitialForm());
  const [invoices, setInvoices] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(invoices));
  }, [invoices]);

  const draftInvoice = useMemo(
    () => ({
      ...form,
      invoiceNumber: "Draft",
      createdAt: new Date().toISOString(),
      createdDateLabel: formatDateLong(new Date()),
      paid: false,
    }),
    [form]
  );

  const selectedInvoice = selectedId ? invoices.find((invoice) => invoice.id === selectedId) : null;
  const previewInvoice = selectedInvoice || draftInvoice;
  const totalDraft = getInvoiceTotal(form.items);
  const validItems = form.items.filter((item) => item.description.trim() && getItemTotal(item) > 0);
  const canCreateInvoice = form.clientName.trim() && validItems.length > 0;
  const filteredInvoices = invoices.filter((invoice) => {
    if (statusFilter === "paid") return invoice.paid;
    if (statusFilter === "unpaid") return !invoice.paid;
    return true;
  });
  const paidCount = invoices.filter((invoice) => invoice.paid).length;
  const unpaidCount = invoices.length - paidCount;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateItem = (id, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    }));
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [
        ...current.items,
        { id: Date.now(), description: "", unit: "", quantity: "1", amount: "" },
      ],
    }));
  };

  const removeItem = (id) => {
    setForm((current) => ({
      ...current,
      items: current.items.length === 1 ? current.items : current.items.filter((item) => item.id !== id),
    }));
  };

  const createInvoice = (event) => {
    event.preventDefault();
    if (!canCreateInvoice) return;

    const total = getInvoiceTotal(form.items);
    const now = new Date();
    const invoice = {
      ...form,
      clientName: form.clientName.trim(),
      subject: form.subject.trim(),
      extraDescription: form.extraDescription.trim(),
      items: form.items
        .filter((item) => item.description.trim() || getItemTotal(item) > 0)
        .map((item) => ({
          ...item,
          description: item.description.trim(),
          quantity: item.quantity || "1",
          amount: String(getItemTotal(item)),
        })),
      id: now.getTime(),
      invoiceNumber: createInvoiceNumber(),
      createdAt: now.toISOString(),
      createdDateLabel: formatDateLong(now),
      spelledAmount: form.spelledAmount || `${toWords(total)} rupiah`,
      total,
      paid: false,
    };

    setInvoices((current) => [invoice, ...current]);
    setSelectedId(invoice.id);
  };

  const resetForm = () => {
    setForm(cloneInitialForm());
    setSelectedId(null);
  };

  const togglePaid = (id) => {
    setInvoices((current) =>
      current.map((invoice) => (invoice.id === id ? { ...invoice, paid: !invoice.paid } : invoice))
    );
  };

  const removeInvoice = (id) => {
    setInvoices((current) => current.filter((invoice) => invoice.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
  };

  const selectHistoryInvoice = (invoice) => {
    setSelectedId((currentId) => (currentId === invoice.id ? null : invoice.id));
  };

  const printInvoice = () => {
    const printWindow = window.open("", "_blank", "width=900,height=1200");
    if (!printWindow) {
      window.print();
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildPrintableInvoiceHtml(previewInvoice));
    printWindow.document.close();
    printWindow.focus();
    printWindow.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  return (
    <div className="p-4 pt-16 sm:p-8 lg:p-10 max-w-[96rem] mx-auto space-y-6 fade-in">
      <style>
        {`
          @media print {
            @page { size: A4; margin: 12mm; }
            body * { visibility: hidden; }
            #invoice-print-area, #invoice-print-area * { visibility: visible; }
            #invoice-print-area {
              position: absolute;
              inset: 0;
              width: 100%;
              background: white;
            }
            #invoice-print-area > div {
              border: 0 !important;
              box-shadow: none !important;
              border-radius: 0 !important;
            }
            #invoice-print-area .invoice-paper {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">Invoice</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight c-strong">Buat Invoice</h1>
          <p className="text-sm c-muted mt-2">
            Kop kantor dan tanda tangan tetap, sementara data client dan rincian biaya bisa disesuaikan.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex items-center gap-2 rounded-lg border c-border px-4 py-2.5 text-sm font-medium c-text transition hover:border-gold hover:text-gold"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
          <button
            type="button"
            onClick={printInvoice}
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2.5 text-sm font-medium text-white transition hover:bg-ink-soft dark:bg-gold dark:text-ink"
          >
            <Printer className="h-4 w-4" />
            Cetak
          </button>
        </div>
      </div>

      <div className="grid gap-6 2xl:grid-cols-[minmax(820px,1fr)_560px]">
        <form onSubmit={createInvoice} className="space-y-5">
          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-medium c-strong">Data Invoice</h2>
                <p className="text-xs c-muted mt-1">Bagian yang berubah dari contoh invoice.</p>
              </div>
              <ReceiptText className="h-5 w-5 text-gold" />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Nama client / kepada</span>
                <input
                  required
                  value={form.clientName}
                  onChange={(event) => updateField("clientName", event.target.value)}
                  className={fieldClass}
                  placeholder="Contoh: PT. Loka Teknologi Indonesia"
                />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Keterangan utama</span>
                <input
                  value={form.subject}
                  onChange={(event) => updateField("subject", event.target.value)}
                  className={fieldClass}
                  placeholder="Contoh: Pengurusan HT atas sertifikat..."
                />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Terbilang</span>
                <input
                  value={form.spelledAmount}
                  onChange={(event) => updateField("spelledAmount", event.target.value)}
                  className={fieldClass}
                  placeholder={totalDraft > 0 ? `Otomatis: ${toWords(totalDraft)} rupiah` : "Otomatis mengikuti total"}
                />
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Keterangan tambahan</span>
                <textarea
                  value={form.extraDescription}
                  onChange={(event) => updateField("extraDescription", event.target.value)}
                  rows={3}
                  className={fieldClass}
                  placeholder="Contoh: Pengurusan sertipikat hak nomor..., kelurahan..., kecamatan..."
                />
              </label>
            </div>
          </div>

          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl font-medium c-strong">Rincian Biaya</h2>
                <p className="text-xs c-muted mt-1">Isi pembayaran, satuan, jumlah, dan total invoice.</p>
              </div>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center gap-2 rounded-lg bg-gold px-3 py-2 text-sm font-medium text-white transition hover:bg-gold-dark"
              >
                <Plus className="h-4 w-4" />
                Item
              </button>
            </div>

            <div className="space-y-4">
              {form.items.map((item, index) => (
                <div key={item.id} className="rounded-lg border c-border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-semibold c-strong">Item {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-wine transition hover:bg-wine/10"
                      aria-label="Hapus item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid gap-3 xl:grid-cols-[minmax(360px,1fr)_200px_96px]">
                    <textarea
                      required={index === 0}
                      value={item.description}
                      onChange={(event) => updateItem(item.id, "description", event.target.value)}
                      rows={3}
                      className={`${fieldClass} min-h-[96px] resize-y leading-relaxed`}
                      placeholder="Contoh: Biaya Pengecekan dan percepatan"
                    />
                    <input
                      value={item.unit}
                      onChange={(event) => updateItem(item.id, "unit", event.target.value)}
                      className={fieldClass}
                      placeholder="Satuan, mis. Rp. 5.000.000,-"
                    />
                    <input
                      value={item.quantity}
                      onChange={(event) => updateItem(item.id, "quantity", event.target.value)}
                      className={fieldClass}
                      placeholder="Qty"
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-ink/[0.03] px-3 py-2 text-xs dark:bg-white/[0.04]">
                    <span className="c-muted">Subtotal item</span>
                    <span className="font-semibold text-gold">{formatCurrency(getItemTotal(item))}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between rounded-lg bg-gold/10 px-4 py-3 text-sm">
              <span className="font-medium c-text">Total sementara</span>
              <span className="font-semibold text-gold">{formatCurrency(totalDraft)}</span>
            </div>
          </div>

          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 md:col-span-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Catatan pembayaran</span>
                <textarea
                  value={form.note}
                  onChange={(event) => updateField("note", event.target.value)}
                  rows={3}
                  className={fieldClass}
                />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Kota tanda tangan</span>
                <input
                  value={form.city}
                  onChange={(event) => updateField("city", event.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="space-y-2">
                <span className="text-xs font-medium uppercase tracking-[0.16em] c-muted">Jabatan bawah tanda tangan</span>
                <input
                  value={form.signerTitle}
                  onChange={(event) => updateField("signerTitle", event.target.value)}
                  className={fieldClass}
                />
              </label>
            </div>

            {!canCreateInvoice && (
              <div className="mt-5 flex items-start gap-2 rounded-lg border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                Isi minimal nama client dan satu rincian biaya dengan nominal.
              </div>
            )}

            <button
              type="submit"
              disabled={!canCreateInvoice}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-gold/20 transition hover:bg-gold-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Buat Invoice
            </button>
          </div>
        </form>

        <div className="space-y-5 2xl:sticky 2xl:top-6 2xl:self-start">
          <div id="invoice-print-area">
            <InvoicePreview invoice={previewInvoice} />
          </div>

          <div className="c-card border rounded-xl p-5 card-elev">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-medium c-strong">History Invoice</h2>
                <p className="text-xs c-muted mt-1">Tandai invoice lunas atau belum lunas.</p>
              </div>
              <FileText className="h-5 w-5 text-gold" />
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-lg bg-ink/[0.03] px-2 py-2 dark:bg-white/[0.04]">
                <div className="font-semibold c-strong">{invoices.length}</div>
                <div className="c-muted">Total</div>
              </div>
              <div className="rounded-lg bg-emerald2/10 px-2 py-2">
                <div className="font-semibold text-emerald2 dark:text-[#7FBF9F]">{paidCount}</div>
                <div className="c-muted">Lunas</div>
              </div>
              <div className="rounded-lg bg-wine/10 px-2 py-2">
                <div className="font-semibold text-wine dark:text-[#E08585]">{unpaidCount}</div>
                <div className="c-muted">Belum</div>
              </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {[
                ["all", "Semua"],
                ["unpaid", "Belum lunas"],
                ["paid", "Lunas"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatusFilter(value)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                    statusFilter === value
                      ? "border-gold bg-gold/10 text-gold"
                      : "c-border c-text hover:border-gold hover:text-gold"
                  }`}
                >
                  <Filter className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {invoices.length === 0 ? (
              <div className="rounded-lg border border-dashed c-border p-6 text-center text-sm c-muted">
                Belum ada invoice. Isi form lalu klik Buat Invoice.
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="rounded-lg border border-dashed c-border p-6 text-center text-sm c-muted">
                Tidak ada invoice pada filter ini.
              </div>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                {filteredInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className={`rounded-lg border p-4 transition ${
                      selectedId === invoice.id ? "border-gold bg-gold/5" : "c-border"
                    }`}
                  >
                      <button
                        type="button"
                      onClick={() => selectHistoryInvoice(invoice)}
                        className="w-full text-left"
                      >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold c-strong">
                            {invoice.clientName || "Tanpa nama client"}
                          </div>
                          <div className="mt-1 text-xs c-muted">
                            {invoice.invoiceNumber} - {invoice.createdDateLabel}
                          </div>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                            invoice.paid
                              ? "bg-emerald2/10 text-emerald2 dark:text-[#7FBF9F]"
                              : "bg-wine/10 text-wine dark:text-[#E08585]"
                          }`}
                        >
                          {invoice.paid ? "Lunas" : "Belum lunas"}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className="c-muted">{invoice.subject || "Tanpa keterangan"}</span>
                        <span className="font-semibold text-gold">{formatCurrency(invoice.total)}</span>
                      </div>
                    </button>

                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => togglePaid(invoice.id)}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border c-border px-3 py-2 text-xs font-medium c-text transition hover:border-gold hover:text-gold"
                      >
                        {invoice.paid ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                        {invoice.paid ? "Sudah lunas" : "Tandai lunas"}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeInvoice(invoice.id)}
                        className="inline-flex items-center justify-center rounded-lg border border-wine/20 px-3 py-2 text-wine transition hover:bg-wine/10"
                        aria-label="Hapus invoice"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
